import React, { useEffect, useState } from 'react';
import { fetchCategories, fetchProducts, fetchVariantGroups, fetchActivePromotions, getCatalogId } from '../lib/api';
import CategoryTile from './CategoryTile';
import ProductCard from './ProductCard';
import { getOrgId } from '../utils/tenantBoot';

/**
 * Check if running in kiosk mode
 */
const isKioskMode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('kiosk') === '1';
};

/**
 * Build a barcode -> promotion map from fetchActivePromotions() data.
 * Single shared implementation reused by every product-loading path
 * (category, brand, product-group) so no view can silently miss promotion data.
 */
const buildBarcodePromotionMap = (promotionsData) => {
  const barcodePromotionMap = {};
  if (!(promotionsData && promotionsData.promotions && promotionsData.group_items)) {
    return barcodePromotionMap;
  }
  const { promotions, groups, group_items } = promotionsData;

  const promoMap = {};
  promotions.forEach(promo => {
    promoMap[promo.promo_id] = promo;
  });

  const groupMap = {};
  (groups || []).forEach(group => {
    const key = `${group.promo_id}_${group.group_id}`;
    groupMap[key] = group;
  });

  group_items.forEach(item => {
    if (!item.barcode) return;
    const promo = promoMap[item.promo_id];
    const rewardGroupKey = Object.keys(groupMap).find(key =>
      key.startsWith(`${item.promo_id}_`) && groupMap[key].kind === 'REWARD'
    );
    const qualifierGroupKey = Object.keys(groupMap).find(key =>
      key.startsWith(`${item.promo_id}_`) && groupMap[key].kind === 'QUALIFIER'
    );

    if (!(promo && rewardGroupKey)) return;
    const rewardGroup = groupMap[rewardGroupKey];
    const qualifierGroup = qualifierGroupKey ? groupMap[qualifierGroupKey] : null;

    let promoText = promo.name;
    if (qualifierGroup && qualifierGroup.min_qty && rewardGroup.reward_type === 'PRICE_OVERRIDE') {
      promoText = `${qualifierGroup.min_qty} for £${rewardGroup.reward_value}`;
    } else if (qualifierGroup && qualifierGroup.min_qty && rewardGroup.reward_type === 'MONEY_OFF') {
      promoText = `${qualifierGroup.min_qty} for £${rewardGroup.reward_value}`;
    } else if (qualifierGroup && qualifierGroup.min_qty && rewardGroup.reward_type === 'PERCENT_OFF') {
      promoText = `Buy ${qualifierGroup.min_qty} Get ${rewardGroup.reward_value}% Off`;
    } else if (rewardGroup.reward_type === 'MONEY_OFF') {
      promoText = `£${rewardGroup.reward_value} Off`;
    } else if (rewardGroup.reward_type === 'PERCENT_OFF') {
      promoText = `${rewardGroup.reward_value}% Off`;
    }

    barcodePromotionMap[item.barcode] = {
      text: promoText,
      promo_id: promo.promo_id,
      promo_name: promo.name,
      group_id: item.group_id,
      min_qty: qualifierGroup?.min_qty || 1,
      reward_type: rewardGroup.reward_type,
      reward_value: rewardGroup.reward_value
    };
  });

  return barcodePromotionMap;
};

/**
 * Attach `promotion` to each product by barcode using the shared map above.
 */
const enrichProductsWithPromotion = (products, barcodePromotionMap) =>
  (products || []).map(product => ({
    ...product,
    promotion: product.barcode ? (barcodePromotionMap[product.barcode] || null) : null
  }));

/**
 * Main Catalog View Component
 * Shows categories or products in a scrollable grid
 * Adapts layout when product detail panel is open
 */
export default function CatalogView({
  selectedCategory,
  selectedBrand,
  selectedProductGroup,
  onCategorySelect,
  onFilterOnlySelect,
  onProductSelect,
  onBackToCategories,
  hasProductPanelOpen,
  variantDisplay = 'grouped', // Receive from parent (App.jsx)
}) {
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // All categories for subcategory filtering
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]); // Products after subcategory filter
  const [selectedSubcategory, setSelectedSubcategory] = useState(null); // null = "All"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cachedProducts, setCachedProducts] = useState({}); // Cache products by category
  const [catalogSettings, setCatalogSettings] = useState({
    show_stock_availability: true,
    low_stock_threshold: 10,
    show_add_to_basket: false,
  });
  // Set only when a Category + Brand combination genuinely has zero matching
  // products - drives the "we may not have X, but here's what we do have"
  // message. Cleared on every other load path.
  const [noExactMatchInfo, setNoExactMatchInfo] = useState(null);
  // variantDisplay is now received as prop from App.jsx

  // Load catalog settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const orgId = getOrgId();
      const baseUrl = import.meta.env.VITE_RCC_BASE || '';
      const response = await fetch(`${baseUrl}/rcc-api/catalog/settings?org_id=${orgId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings) {
          setCatalogSettings({
            show_stock_availability: data.settings.show_stock_availability !== false,
            low_stock_threshold: data.settings.low_stock_threshold || 10,
            show_add_to_basket: data.settings.show_add_to_basket || false,
          });
        }
      }
    } catch (err) {
      console.warn('[CatalogView] Failed to load settings, using defaults:', err);
    }
  };

  // Load all categories once on mount for subcategory filtering
  useEffect(() => {
    loadAllCategories();
  }, []);

  useEffect(() => {
    setNoExactMatchInfo(null);
    const activeFilterCount = [selectedCategory, selectedBrand, selectedProductGroup].filter(Boolean).length;
    if (activeFilterCount >= 2) {
      // Any combination of 2+ filters (Category, Brand, Product Group) always
      // combines as an AND filter, never one replacing another.
      loadProductsByFilters({ category: selectedCategory, brand: selectedBrand, group: selectedProductGroup });
      setSelectedSubcategory(null);
    } else if (selectedBrand) {
      loadProductsByBrand(selectedBrand.brand_id);
      setSelectedSubcategory(null);
    } else if (selectedProductGroup) {
      loadProductsByGroup(selectedProductGroup.group_id);
      setSelectedSubcategory(null);
    } else if (!selectedCategory) {
      loadCategories();
      setSelectedSubcategory(null); // Reset subcategory filter
    } else {
      loadProducts(selectedCategory.category_id);
      setSelectedSubcategory(null); // Reset subcategory filter when category changes
    }
  }, [selectedCategory, selectedBrand, selectedProductGroup, variantDisplay]);

  // Filter products when subcategory filter changes
  useEffect(() => {
    if (!selectedSubcategory) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(p => p.category_id === selectedSubcategory);
      setFilteredProducts(filtered);
    }
  }, [selectedSubcategory, products]);

  // Cache is now keyed by category + variant mode, so no need to clear on mode change

  const loadAllCategories = async () => {
    try {
      const mode = isKioskMode() ? 'catalog' : 'ecommerce';
      const data = await fetchCategories(mode);
      setAllCategories(data || []);
    } catch (err) {
      console.error('Failed to load all categories:', err);
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const mode = isKioskMode() ? 'catalog' : 'ecommerce';
      const data = await fetchCategories(mode);
      // Filter to show only parent categories (parent_id === null)
      const parentCategories = (data || []).filter(cat => !cat.parent_id);
      setCategories(parentCategories);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async (categoryId) => {
    // Check cache first (PWA optimization) - include variantDisplay in cache key
    const cacheKey = `${categoryId}_${variantDisplay}`;
    if (cachedProducts[cacheKey]) {
      setProducts(cachedProducts[cacheKey]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const mode = isKioskMode() ? 'catalog' : 'ecommerce';
      
      // Fetch products, variant groups, and promotions in parallel
      // Allow promotions to fail gracefully without blocking product display
      const [productsData, variantGroupsData, promotionsData] = await Promise.all([
        fetchProducts(categoryId, mode, variantDisplay),
        fetchVariantGroups(getOrgId()),
        fetchActivePromotions().catch(err => {
          console.warn('[CatalogView] Failed to load promotions (non-critical):', err);
          return { promotions: [], groups: [], group_items: [] };
        })
      ]);
      
      const productsList = productsData || [];
      const groups = variantGroupsData?.groups || [];
      
      console.log('[CatalogView] Products:', productsList.length);
      console.log('[CatalogView] Variant groups:', groups.length);
      console.log('[CatalogView] Promotions data received:', promotionsData);
      
      // Log product barcodes for debugging
      console.log('[CatalogView] Product barcodes:', productsList.map(p => ({ 
        name: p.product_name, 
        barcode: p.barcode 
      })));
      
      // Build a map of product_id -> variant group data
      const productVariantMap = {};
      groups.forEach(group => {
        if (group.products && group.products.length > 0) {
          group.products.forEach(prod => {
            const productId = prod.product_id;
            
            // Debug logging
            if (productsList.some(p => p.product_id === productId)) {
              console.log(`[CatalogView] Mapping variant for product ${productId}:`, {
                groupName: group.group_name,
                attributes: prod.attributes
              });
            }
            
            if (!productVariantMap[productId]) {
              productVariantMap[productId] = {
                groupName: group.group_name,
                groupId: group.group_id,
                attributes: prod.attributes || [],
                variants: group.products.filter(p => p.product_id !== productId)
              };
            }
          });
        }
      });
      
      console.log('[CatalogView] Product variant map:', productVariantMap);
      
      // Build barcode-to-promotion map (shared implementation, reused by brand/group loaders too)
      const barcodePromotionMap = buildBarcodePromotionMap(promotionsData);
      console.log('[CatalogView] Barcode promotion map:', barcodePromotionMap);
      console.log('[CatalogView] Total barcodes in promotion map:', Object.keys(barcodePromotionMap).length);
      
      // Enrich products with variant data and promotion info
      const enrichedProducts = productsList.map(product => {
        const variantData = productVariantMap[product.product_id];
        
        // Enrich variants with promotion data
        let enrichedVariantData = null;
        if (variantData) {
          console.log(`[CatalogView] Product "${product.product_name}" has variant data:`, variantData);
          
          // Add promotion info and stock data to each variant
          const enrichedVariants = (variantData.variants || []).map(variant => {
            // Find the variant's barcode and stock from the productsList
            const variantProduct = productsList.find(p => p.product_id === variant.product_id);
            const variantBarcode = variantProduct?.barcode || variant.barcode;
            const variantPromotion = variantBarcode ? barcodePromotionMap[variantBarcode] : null;
            const variantQtyOnHand = variantProduct?.qty_on_hand;
            
            if (variantPromotion) {
              console.log(`[CatalogView] ✅ Variant "${variant.product_name}" has promotion:`, variantPromotion);
            }
            
            return {
              ...variant,
              barcode: variantBarcode,
              promotion: variantPromotion || null,
              qty_on_hand: variantQtyOnHand
            };
          });
          
          enrichedVariantData = {
            ...variantData,
            variants: enrichedVariants
          };
        }
        
        // Add promotion if product barcode matches
        const promotion = product.barcode ? barcodePromotionMap[product.barcode] : null;
        if (product.barcode) {
          console.log(`[CatalogView] Checking product "${product.product_name}" barcode: "${product.barcode}"`, {
            in_map: product.barcode in barcodePromotionMap,
            promotion: promotion
          });
        }
        if (promotion) {
          console.log(`[CatalogView] ✅✅✅ Product "${product.product_name}" (${product.barcode}) has promotion: "${promotion}"`);
        }
        
        return {
          ...product,
          variantData: enrichedVariantData || null,
          promotion: promotion || null
        };
      });
      
      console.log("[CatalogView] Enriched products:", enrichedProducts);
      setProducts(enrichedProducts);
      
      // Cache enriched products with variant display mode in key
      const cacheKey = `${categoryId}_${variantDisplay}`;
      setCachedProducts(prev => ({
        ...prev,
        [cacheKey]: enrichedProducts,
      }));
    } catch (err) {
      console.error('Failed to load products:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProductsByBrand = async (brandId) => {
    try {
      setLoading(true);
      setError(null);
      const baseUrl = import.meta.env.VITE_RCC_BASE || '';
      const mode = isKioskMode() ? 'catalog' : 'ecommerce';
      const orgId = getOrgId();
      const catalogId = await getCatalogId();
      const catalogParam = catalogId ? `&catalog_id=${catalogId}` : '';
      
      // Fetch products for this brand, and promotions (same source used by the
      // category path) so brand-filtered results get the same promotion data.
      const [response, promotionsData] = await Promise.all([
        fetch(`${baseUrl}/rcc-api/brands/${brandId}/products?mode=${mode}&org_id=${orgId}${catalogParam}`),
        fetchActivePromotions().catch(err => {
          console.warn('[CatalogView] Failed to load promotions for brand view (non-critical):', err);
          return { promotions: [], groups: [], group_items: [] };
        })
      ]);
      if (!response.ok) throw new Error('Failed to load brand products');
      
      const productsData = await response.json();
      const productsList = productsData.products || [];
      
      console.log('[CatalogView] Brand products:', productsList.length);
      
      const barcodePromotionMap = buildBarcodePromotionMap(promotionsData);
      const enrichedProducts = enrichProductsWithPromotion(productsList, barcodePromotionMap);
      setProducts(enrichedProducts);
      setFilteredProducts(enrichedProducts);
    } catch (err) {
      console.error('Failed to load brand products:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ANY combination of 2+ active filters (Category, Brand, Product Group)
  // always combines as an AND filter - none of them replace another.
  // Fetches each active filter's OWN full candidate set in parallel, then
  // intersects them by product_id. If the intersection is empty, falls back
  // to whichever single active filter's own set actually has products
  // (first match, in Category > Brand > Product Group priority order),
  // along with a message built entirely from the live filter names/labels -
  // nothing about a specific filter type, brand, category or product is
  // ever hardcoded, so this works identically for any combination.
  const loadProductsByFilters = async ({ category, brand, group }) => {
    try {
      setLoading(true);
      setError(null);
      const mode = isKioskMode() ? 'catalog' : 'ecommerce';
      const orgId = getOrgId();
      const baseUrl = import.meta.env.VITE_RCC_BASE || '';
      const catalogId = await getCatalogId();
      const catalogParam = catalogId ? `&catalog_id=${catalogId}` : '';

      const promotionsPromise = fetchActivePromotions().catch(err => {
        console.warn('[CatalogView] Failed to load promotions for combined filter view (non-critical):', err);
        return { promotions: [], groups: [], group_items: [] };
      });

      const [categoryList, brandData, groupData, promotionsData] = await Promise.all([
        category ? (fetchProducts(category.category_id, mode, variantDisplay) || []) : Promise.resolve(null),
        brand
          ? fetch(`${baseUrl}/rcc-api/brands/${brand.brand_id}/products?mode=${mode}&org_id=${orgId}${catalogParam}`).then(r => r.ok ? r.json() : { products: [] })
          : Promise.resolve(null),
        group
          ? fetch(`${baseUrl}/rcc-api/product-groups/${group.group_id}/products?mode=${mode}&org_id=${orgId}${catalogParam}`).then(r => r.ok ? r.json() : { products: [] })
          : Promise.resolve(null),
        promotionsPromise,
      ]);

      const barcodePromotionMap = buildBarcodePromotionMap(promotionsData);

      // One entry per ACTIVE filter, each with its own independent product
      // set. Order here is only the FALLBACK priority (Brand/Product Group
      // preferred over Category when the combination has zero matches,
      // since those are usually the more specific filter the customer just
      // changed) - it has no effect on the intersection itself.
      const activeSets = [
        brand && { label: 'Brand', name: brand.name, products: enrichProductsWithPromotion(brandData?.products || [], barcodePromotionMap) },
        group && { label: 'Product Group', name: group.group_name, products: enrichProductsWithPromotion(groupData?.products || [], barcodePromotionMap) },
        category && { label: 'Category', name: category.name, products: enrichProductsWithPromotion(categoryList || [], barcodePromotionMap) },
      ].filter(Boolean);

      // Intersect every active set's products by product_id.
      let combined = activeSets[0].products;
      for (let i = 1; i < activeSets.length; i++) {
        const ids = new Set(activeSets[i].products.map(p => p.product_id));
        combined = combined.filter(p => ids.has(p.product_id));
      }

      if (combined.length > 0) {
        setProducts(combined);
        setFilteredProducts(combined);
        setNoExactMatchInfo(null);
        return;
      }

      // No products match ALL active filters together - fall back to the
      // first active filter (by priority) that has any products of its own.
      const fallbackSet = activeSets.find(s => s.products.length > 0);
      if (fallbackSet) {
        setProducts(fallbackSet.products);
        setFilteredProducts(fallbackSet.products);
        const otherNames = activeSets.filter(s => s !== fallbackSet).map(s => s.name).join(' + ');
        setNoExactMatchInfo({ otherNames, fallbackName: fallbackSet.name });
      } else {
        setProducts([]);
        setFilteredProducts([]);
        setNoExactMatchInfo(null);
      }
    } catch (err) {
      console.error('Failed to load combined-filter products:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProductsByGroup = async (groupId) => {
    try {
      setLoading(true);
      setError(null);
      const baseUrl = import.meta.env.VITE_RCC_BASE || '';
      const mode = isKioskMode() ? 'catalog' : 'ecommerce';
      const orgId = getOrgId();
      const catalogId = await getCatalogId();
      const catalogParam = catalogId ? `&catalog_id=${catalogId}` : '';
      
      // Fetch products assigned to this group, and promotions (same source used by
      // the category path) so group-filtered results get the same promotion data.
      const [response, promotionsData] = await Promise.all([
        fetch(`${baseUrl}/rcc-api/product-groups/${groupId}/products?mode=${mode}&org_id=${orgId}${catalogParam}`),
        fetchActivePromotions().catch(err => {
          console.warn('[CatalogView] Failed to load promotions for group view (non-critical):', err);
          return { promotions: [], groups: [], group_items: [] };
        })
      ]);
      if (!response.ok) throw new Error('Failed to load group products');
      
      const productsData = await response.json();
      const productsList = productsData.products || [];
      
      console.log('[CatalogView] Product group products:', productsList.length);
      
      const barcodePromotionMap = buildBarcodePromotionMap(promotionsData);
      const enrichedProducts = enrichProductsWithPromotion(productsList, barcodePromotionMap);
      setProducts(enrichedProducts);
      setFilteredProducts(enrichedProducts);
    } catch (err) {
      console.error('Failed to load product group products:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    if (!selectedCategory) {
      loadCategories();
    } else {
      // Clear cache for this category and current variant mode, then reload
      const cacheKey = `${selectedCategory.category_id}_${variantDisplay}`;
      setCachedProducts(prev => {
        const newCache = { ...prev };
        delete newCache[cacheKey];
        return newCache;
      });
      loadProducts(selectedCategory.category_id);
    }
  };

  // Get subcategories for the selected category
  const subcategories = selectedCategory
    ? allCategories.filter(cat => cat.parent_id === selectedCategory.category_id)
    : [];

  // Debug logging
  console.log('[CatalogView] Rendering with:', {
    selectedCategory: selectedCategory?.name || null,
    selectedBrand: selectedBrand?.name || null,
    selectedProductGroup: selectedProductGroup?.group_name || null
  });

  return (
    <div className={`catalog-view ${hasProductPanelOpen ? 'with-panel' : ''}`}>
      <div className="catalog-content">
        {/* Title Bar - Show current filter(s). Generic: lists every ACTIVE
            filter (Category/Brand/Product Group), not just Brand - stays
            correct for any combination without hardcoding a specific pair. */}
        {(selectedBrand || selectedProductGroup) && (() => {
          const activeFilters = [
            selectedCategory && { label: 'Category', name: selectedCategory.name, type: 'category', value: selectedCategory },
            selectedBrand && { label: 'Brand', name: selectedBrand.name, type: 'brand', value: selectedBrand },
            selectedProductGroup && { label: 'Product Group', name: selectedProductGroup.group_name, type: 'group', value: selectedProductGroup },
          ].filter(Boolean);
          // Each segment reads a size smaller than the previous one (first
          // filter largest, last filter smallest) - clamp() keeps the same
          // decreasing proportions across all screen sizes instead of a
          // fixed px scale.
          const segmentFontSizes = [
            'clamp(15px, 2.2vw, 22px)',
            'clamp(13px, 1.85vw, 19px)',
            'clamp(11px, 1.5vw, 16px)',
          ];
          return (
            <div className="catalog-header">
              <h2 className="catalog-title">
                {activeFilters.map((f, i) => (
                  <span key={f.label} style={{ fontSize: segmentFontSizes[i] || segmentFontSizes[segmentFontSizes.length - 1] }}>
                    {i > 0 && <span className="breadcrumb-separator">›</span>}
                    <span
                      className="breadcrumb-segment"
                      style={{ cursor: 'pointer' }}
                      onClick={() => onFilterOnlySelect && onFilterOnlySelect(f.type, f.value)}
                      title={`Show only ${f.name}`}
                    >
                      {f.name}
                    </span>
                  </span>
                ))}
              </h2>
            </div>
          );
        })()}

        {/* Subcategory Filter Chips - Show when parent category has subcategories */}
        {selectedCategory && subcategories.length > 0 && (
          <div className="subcategory-filter-bar">
            <div className="filter-label">Filter by:</div>
            <div className="filter-chips">
              <button
                className={`filter-chip ${!selectedSubcategory ? 'active' : ''}`}
                onClick={() => setSelectedSubcategory(null)}
              >
                All ({products.length})
              </button>
              {subcategories.map(subcat => {
                const count = products.filter(p => p.category_id === subcat.category_id).length;
                return (
                  <button
                    key={subcat.category_id}
                    className={`filter-chip ${selectedSubcategory === subcat.category_id ? 'active' : ''}`}
                    onClick={() => setSelectedSubcategory(subcat.category_id)}
                  >
                    {subcat.name} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="catalog-message">
            <div className="loading-spinner"></div>
            <p>Loading {selectedBrand ? 'brand products' : selectedProductGroup ? 'collection products' : selectedCategory ? 'products' : 'categories'}...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="catalog-error">
            <div className="error-content">
              <div className="error-title">⚠️ Error loading {selectedBrand ? 'brand products' : selectedProductGroup ? 'collection products' : selectedCategory ? 'products' : 'categories'}</div>
              <div className="error-message">{error}</div>
              <button className="retry-btn" onClick={handleRetry}>
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && !selectedCategory && !selectedBrand && !selectedProductGroup && categories.length === 0 && (
          <div className="catalog-message">
            <p>No categories available yet.</p>
          </div>
        )}

        {!loading && !error && (selectedCategory || selectedBrand || selectedProductGroup) && products.length === 0 && (
          <div className="catalog-message">
            <p>No products available{selectedBrand ? ' for this brand' : selectedProductGroup ? ' in this collection' : ' in this category'}.</p>
          </div>
        )}

        {!loading && !error && selectedCategory && products.length > 0 && filteredProducts.length === 0 && (
          <div className="catalog-message">
            <p>No products found in the selected subcategory.</p>
          </div>
        )}

        {/* Categories Grid - Hierarchical Display */}
        {!loading && !error && !selectedCategory && !selectedBrand && !selectedProductGroup && categories.length > 0 && (() => {
          const topLevel = categories.filter(cat => !cat.parent_id);
          const withParent = categories.filter(cat => cat.parent_id);
          
          const grouped = withParent.reduce((acc, subcat) => {
            const parentId = subcat.parent_id;
            if (!acc[parentId]) acc[parentId] = [];
            acc[parentId].push(subcat);
            return acc;
          }, {});
          
          return (
            <div className="category-grid">
              {/* Top-level categories first */}
              {topLevel.map((category) => (
                <div
                  key={category.category_id}
                  onClick={() => onCategorySelect(category)}
                  style={{ cursor: 'pointer' }}
                >
                  <CategoryTile category={category} />
                </div>
              ))}
              
              {/* Subcategories grouped under parents */}
              {Object.entries(grouped).map(([parentId, subcats]) => {
                const parent = categories.find(c => c.category_id === parentId);
                return (
                  <div key={`group-${parentId}`} style={{ gridColumn: '1 / -1', marginTop: 16 }}>
                    <div style={{ 
                      fontSize: 13, 
                      fontWeight: 600, 
                      color: 'var(--color-text-secondary)', 
                      marginBottom: 8,
                      paddingLeft: 12,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {parent?.name || 'Subcategories'}
                    </div>
                    <div className="subcategory-grid" style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: 12 
                    }}>
                      {subcats.map(subcat => (
                        <div
                          key={subcat.category_id}
                          onClick={() => onCategorySelect(subcat)}
                          style={{ 
                            cursor: 'pointer',
                            paddingLeft: 8,
                            borderLeft: '3px solid var(--color-primary)',
                            opacity: 0.9
                          }}
                        >
                          <CategoryTile category={subcat} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Combined filters had zero exact matches together - explain what
            we're showing instead (whichever active filter's own products
            actually have results). Generic for any combination/count of
            active filters, nothing hardcoded to a specific filter type. */}
        {!loading && !error && noExactMatchInfo && (
          <div className="catalog-message">
            <p>
              We may not have {noExactMatchInfo.otherNames} for {noExactMatchInfo.fallbackName}, but here is what we have for {noExactMatchInfo.fallbackName}:
            </p>
          </div>
        )}

        {/* Products Grid */}
        {!loading && !error && (selectedCategory || selectedBrand || selectedProductGroup) && filteredProducts.length > 0 && (
          <div className="product-grid">
            {filteredProducts.map((product) => (
              <div
                key={product.sku_id}
                className="product-card"
                data-product="true"
                onClick={() => onProductSelect(product)}
                style={{ cursor: 'pointer' }}
              >
                <ProductCard 
                  product={product} 
                  hideButton={!catalogSettings.show_add_to_basket}
                  catalogSettings={catalogSettings}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
